// DynamicLanding.js — Orquestador de secciones dinámicas con fuentes globales
import React, { useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import SectionRenderer from './SectionRenderer';
import { useIdioma } from '../../contexts/IdiomaContext';
import { buildBg } from './bgHelpers';
import { BACKEND_BASE } from '../../services/api';

// Google Fonts a cargar dinámicamente. Roboto e Inter ya no están: van
// auto-alojadas en public/fonts (ver fuentes.css).
const GOOGLE_FONTS_MAP = {
  'Open Sans': 'Open+Sans:wght@300;400;600;700',
  'Montserrat': 'Montserrat:wght@300;400;600;700;900',
  'Poppins': 'Poppins:wght@300;400;600;700',
  'Lato': 'Lato:wght@300;400;700',
  'Playfair Display': 'Playfair+Display:wght@400;700',
  'Caveat': 'Caveat:wght@400;600;700',
  'Dancing Script': 'Dancing+Script:wght@400;600;700',
};

export const LandingWrapper = styled.div`
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  /* Tamaño base del texto de la landing. Sin la clave no se emite nada, así que
     los proyectos existentes heredan lo de siempre del documento. */
  ${p => p.$size ? `font-size: ${p.$size};` : ''}
  /* Ancho del contenedor de la web (landing_config.ancho_contenedor): las secciones que
     lo usan leen esta variable; sin la clave no se emite y cada una usa el suyo. */
  ${p => p.$ancho ? `--ancho-contenedor: ${p.$ancho};` : ''}
  width: 100%;
  min-height: 100vh;
  position: relative;
  ${p => p.$bg && `background: ${p.$bg};`}
  ${p => p.$bgImage && `
    background-image: ${p.$overlay ? `linear-gradient(${p.$overlay}, ${p.$overlay}), ` : ''}url(${p.$bgImage});
    background-size: ${p.$bgSize || 'cover'};
    background-position: ${p.$bgPos || 'center'};
    background-repeat: ${p.$bgRepeat || 'no-repeat'};
    background-attachment: ${p.$bgAttach || 'scroll'};
  `}
`;

// Campos de fondo que forman un "bloque" (se heredan/sobre-escriben juntos)
const BG_KEYS = [
  'fondo_transparente', 'tipo_fondo', 'color_fondo', 'color_fondo_2',
  'direccion_degradado', 'imagen_fondo', 'tamano_fondo', 'posicion_fondo',
  'repeat_fondo', 'attach_fondo', 'overlay_fondo',
];

const DynamicLanding = ({ secciones, proyecto, openLogin, openRegister, configOverride }) => {
  const { idioma } = useIdioma();
  // Cascada de configuración: global del proyecto (landing_config) como base, y si
  // esta página trae un override (configOverride), gana por bloque:
  //  - Fuentes: la página gana si define la suya; si no, hereda la global.
  //  - Fondo: si la página define SU propio fondo (color/imagen/degradado/transparente),
  //    se usa el de la página; si no, se hereda el global.
  // Las secciones sin fondo propio siguen mostrando este fondo efectivo (global o de la página).
  const landingConfig = useMemo(() => {
    const base = proyecto?.config_json?.landing_config || {};
    const ov = configOverride || {};
    const merged = { ...base };
    if (ov.fuente_principal) merged.fuente_principal = ov.fuente_principal;
    if (ov.fuente_titulos) merged.fuente_titulos = ov.fuente_titulos;
    if (ov.ancho_contenedor) merged.ancho_contenedor = ov.ancho_contenedor;
    const pageHasBg = !!(ov.imagen_fondo || ov.color_fondo || ov.fondo_transparente
      || ov.tipo_fondo === 'degradado');
    if (pageHasBg) {
      BG_KEYS.forEach(k => { merged[k] = ov[k]; });
    }
    return merged;
  }, [proyecto, configOverride]);

  // Resolve translatable field: string → as-is, {es,en,pt} → pick by idioma
  const tl = useCallback((value, fallback = '') => {
    if (value == null) return fallback;
    if (typeof value === 'string') return value || fallback;
    if (typeof value === 'object') return value[idioma] || value['es'] || fallback;
    return fallback;
  }, [idioma]);

  // Cargar Google Fonts si se usan
  useEffect(() => {
    const fontsToLoad = new Set();
    const fuentePrincipal = landingConfig.fuente_principal || '';
    const fuenteTitulos = landingConfig.fuente_titulos || '';

    [fuentePrincipal, fuenteTitulos].forEach(f => {
      if (f) {
        // Extraer nombre de la fuente (sin fallbacks)
        const name = f.split(',')[0].replace(/['"]/g, '').trim();
        if (GOOGLE_FONTS_MAP[name]) {
          fontsToLoad.add(GOOGLE_FONTS_MAP[name]);
        }
      }
    });

    // También revisar fuentes dentro de las secciones (hero, header/menú, etc.)
    (secciones || []).forEach(s => {
      if (s.config) {
        [s.config.fuente_titulo, s.config.fuente_subtitulo, s.config.fuente_menu,
         s.config.fuente_contenido, s.config.fuente_texto, s.config.fuente].forEach(f => {
          if (f) {
            const name = f.split(',')[0].replace(/['"]/g, '').trim();
            if (GOOGLE_FONTS_MAP[name]) {
              fontsToLoad.add(GOOGLE_FONTS_MAP[name]);
            }
          }
        });
      }
    });

    if (fontsToLoad.size > 0) {
      const existing = document.getElementById('dynamic-landing-fonts');
      if (existing) existing.remove();
      const link = document.createElement('link');
      link.id = 'dynamic-landing-fonts';
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?${[...fontsToLoad].map(f => `family=${f}`).join('&')}&display=swap`;
      document.head.appendChild(link);
    }

    return () => {
      const el = document.getElementById('dynamic-landing-fonts');
      if (el) el.remove();
    };
  }, [secciones, landingConfig.fuente_principal, landingConfig.fuente_titulos]);

  // El header siempre va primero y el footer siempre al final, sin importar su
  // `orden` (clave para que un header/footer GLOBAL se ubique bien en la landing).
  // El resto se ordena por `orden`.
  const rankTipo = (t) => (t === 'header' ? -1 : t === 'footer' ? 1 : 0);
  const visibles = (secciones || [])
    .filter(s => s.visible !== false)
    .sort((a, b) => {
      const r = rankTipo(a.tipo) - rankTipo(b.tipo);
      return r !== 0 ? r : (a.orden ?? 0) - (b.orden ?? 0);
    });

  if (visibles.length === 0) return null;

  const globalFont = landingConfig.fuente_principal || "'Myriad Pro', sans-serif";
  // Fondo global de la landing: color/degradado + imagen + overlay
  const fondoBg = buildBg(landingConfig, null);
  const fondoImg = landingConfig.imagen_fondo
    ? (landingConfig.imagen_fondo.startsWith('http') ? landingConfig.imagen_fondo : `${BACKEND_BASE}/${landingConfig.imagen_fondo}`)
    : null;

  return (
    <LandingWrapper
      $font={globalFont}
      $size={landingConfig.tamano_texto}
      $ancho={landingConfig.ancho_contenedor}
      $bg={fondoBg}
      $bgImage={fondoImg}
      $bgSize={landingConfig.tamano_fondo}
      $bgPos={landingConfig.posicion_fondo}
      $bgRepeat={landingConfig.repeat_fondo}
      $bgAttach={landingConfig.attach_fondo}
      $overlay={landingConfig.overlay_fondo}
    >
      {visibles.map(seccion => (
        <SectionRenderer
          key={seccion.id}
          seccion={seccion}
          proyecto={proyecto}
          openLogin={openLogin}
          openRegister={openRegister}
          tl={tl}
        />
      ))}
    </LandingWrapper>
  );
};

export default DynamicLanding;
